import * as XLSX from "xlsx";

export const CHECK_STATUSES = {
  MATCHED: "Uyumlu",
  DAY_DIFFERENCE: "Gün farkı var",
  MISSING_IN_SGK: "SGK’da yok",
  MISSING_IN_REPORT: "Bizim raporda yok",
  REVIEW: "Eşleşme kontrol edilmeli"
};

const TURKISH_CHAR_MAP = {
  Ç: "C",
  Ğ: "G",
  İ: "I",
  I: "I",
  Ö: "O",
  Ş: "S",
  Ü: "U"
};

export const normalizePersonName = (value) =>
  String(value || "")
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/[ÇĞİIÖŞÜ]/g, (char) => TURKISH_CHAR_MAP[char] || char)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeHeader = (value) =>
  String(value || "")
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/[ÇĞİIÖŞÜ]/g, (char) => TURKISH_CHAR_MAP[char] || char)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const normalizeTcIdentity = (value) => {
  const raw = String(value ?? "")
    .trim()
    .replace(/[Xx•●]/g, "*")
    .replace(/[^\d*]/g, "");
  const digits = raw.replace(/\D/g, "");

  if (/^[1-9]\d{10}$/.test(digits) && !raw.includes("*")) {
    return { raw: digits, full: digits, pattern: digits, masked: false };
  }

  if (raw.includes("*") && raw.length >= 7 && raw.length <= 13) {
    return { raw, full: "", pattern: raw, masked: true };
  }

  return { raw: String(value ?? "").trim(), full: "", pattern: raw, masked: false };
};

const identitiesCompatible = (leftValue, rightValue) => {
  const left = normalizeTcIdentity(leftValue);
  const right = normalizeTcIdentity(rightValue);

  if (left.full && right.full) return left.full === right.full;
  if (!left.pattern || !right.pattern) return false;

  if (left.pattern.length === right.pattern.length) {
    for (let index = 0; index < left.pattern.length; index += 1) {
      const leftChar = left.pattern[index];
      const rightChar = right.pattern[index];
      if (leftChar !== "*" && rightChar !== "*" && leftChar !== rightChar) return false;
    }
    return true;
  }

  const masked = left.masked ? left.pattern : right.masked ? right.pattern : "";
  const full = left.full || right.full;
  if (!masked || !full) return false;

  const prefix = masked.match(/^\d+/)?.[0] || "";
  const suffix = masked.match(/\d+$/)?.[0] || "";
  return (!prefix || full.startsWith(prefix)) && (!suffix || full.endsWith(suffix));
};

const parseDayValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value >= 0 && value <= 31 ? Math.round(value) : null;
  }

  const text = String(value ?? "").trim().replace(",", ".");
  const match = text.match(/^-?\d+(?:\.\d+)?$/);
  if (!match) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 31
    ? Math.round(parsed)
    : null;
};

const findColumnIndex = (headers, matcher) =>
  headers.findIndex((header) => matcher(normalizeHeader(header)));

const getRecordKey = (record) => {
  const identity = normalizeTcIdentity(record.tcKimlikNo);
  return identity.full
    ? `tc:${identity.full}`
    : identity.pattern
      ? `masked:${identity.pattern}`
      : `name:${normalizePersonName(record.personel)}`;
};

const aggregateRecords = (records, source) => {
  const grouped = new Map();

  records.forEach((record) => {
    const key = getRecordKey(record);
    if (!key || key === "name:") return;
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, {
        ...record,
        id: `${source}-${grouped.size + 1}`,
        source,
        duplicateCount: 1,
        sourceRows: [record.sourceRow].filter(Boolean)
      });
      return;
    }

    existing.duplicateCount += 1;
    existing.sourceRows.push(record.sourceRow);
    if (record.days !== null && record.days !== undefined) {
      existing.days = Number(existing.days || 0) + Number(record.days);
    }
    if (!existing.personel && record.personel) existing.personel = record.personel;
    if (!existing.tcKimlikNo && record.tcKimlikNo) existing.tcKimlikNo = record.tcKimlikNo;
    if (record.confidence === "low") existing.confidence = "low";
  });

  return [...grouped.values()];
};

export const parseExemptionReportWorkbook = (workbook) => {
  const candidates = [];

  workbook.SheetNames.forEach((sheetName) => {
    const worksheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: "",
      raw: false
    });

    rows.forEach((row, rowIndex) => {
      const dayColumn = findColumnIndex(row, (header) =>
        header.includes("5746") && header.includes("GUN")
      );
      const tcColumn = findColumnIndex(row, (header) =>
        header === "TCKIMLIKNO" || header.includes("TC KIMLIK")
      );
      const nameColumn = findColumnIndex(row, (header) =>
        ["PERSONEL", "AD SOYAD", "ADI SOYADI", "PERSONEL AD SOYAD"].includes(header)
      );

      if (dayColumn >= 0 && (tcColumn >= 0 || nameColumn >= 0)) {
        candidates.push({ sheetName, rows, headerRowIndex: rowIndex, dayColumn, tcColumn, nameColumn });
      }
    });
  });

  if (!candidates.length) {
    throw new Error("Excel içinde Personel/TCKimlikNo ve 5746 Gün Sayısı başlıkları birlikte bulunamadı.");
  }

  const candidate = candidates[0];
  const records = [];

  for (let rowIndex = candidate.headerRowIndex + 1; rowIndex < candidate.rows.length; rowIndex += 1) {
    const row = candidate.rows[rowIndex] || [];
    const personel = String(row[candidate.nameColumn] || "").trim();
    const tcKimlikNo = String(row[candidate.tcColumn] || "").trim();
    const days = parseDayValue(row[candidate.dayColumn]);

    if (!personel && !tcKimlikNo) continue;
    if (days === null && !normalizePersonName(personel)) continue;

    records.push({
      personel,
      tcKimlikNo,
      days,
      confidence: days === null ? "low" : "high",
      sourceRow: `${candidate.sheetName}!${rowIndex + 1}`
    });
  }

  const aggregated = aggregateRecords(records, "report");
  if (!aggregated.length) {
    throw new Error("Excel içinde karşılaştırılabilecek personel satırı bulunamadı.");
  }

  return {
    records: aggregated,
    sheetName: candidate.sheetName,
    totalDays: aggregated.reduce((sum, item) => sum + Number(item.days || 0), 0),
    warnings: aggregated
      .filter((item) => item.duplicateCount > 1)
      .map((item) => `${item.personel || item.tcKimlikNo} birden fazla satırda bulundu; günler toplandı.`)
  };
};

export const readExemptionReportFile = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", raw: false, cellDates: false });
  return parseExemptionReportWorkbook(workbook);
};

const groupPdfItemsIntoLines = (items) => {
  const lines = [];

  items.forEach((item) => {
    const text = String(item.str || "").trim();
    if (!text) return;
    const x = Number(item.transform?.[4] || 0);
    const y = Number(item.transform?.[5] || 0);
    let line = lines.find((candidate) => Math.abs(candidate.y - y) <= 2.5);
    if (!line) {
      line = { y, tokens: [] };
      lines.push(line);
    }
    line.tokens.push({ text, x, y, width: Number(item.width || 0) });
  });

  return lines
    .map((line) => ({
      ...line,
      tokens: line.tokens.sort((left, right) => left.x - right.x)
    }))
    .sort((left, right) => right.y - left.y);
};

const tokenIsDay = (token) => parseDayValue(token.text) !== null && /^\d{1,2}$/.test(token.text.trim());

const extractNameAfterIdentity = (tokens, identityIndex, dayToken) => {
  const upperBoundary = dayToken?.x ?? Number.POSITIVE_INFINITY;
  const nameTokens = [];

  for (let index = identityIndex + 1; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (token.x >= upperBoundary) break;
    if (/\d/.test(token.text)) {
      if (nameTokens.length) break;
      continue;
    }
    const normalized = normalizePersonName(token.text);
    if (!normalized || ["KANUN", "GUN", "TOPLAM"].includes(normalized)) continue;
    nameTokens.push(token.text);
  }

  return nameTokens.join(" ").replace(/\s+/g, " ").trim();
};

const detectColumnPositions = (lines) => {
  for (const line of lines) {
    const normalizedLine = normalizeHeader(line.tokens.map((token) => token.text).join(" "));
    if (!normalizedLine.includes("GUN")) continue;
    const dayToken = line.tokens.find((token) => normalizeHeader(token.text) === "GUN");
    const nameToken = line.tokens.find((token) => {
      const normalized = normalizeHeader(token.text);
      return normalized.includes("AD SOYAD") || normalized === "PERSONEL" || normalized === "ADI SOYADI";
    });
    if (dayToken) return { dayX: dayToken.x, nameX: nameToken?.x ?? null };
  }
  return { dayX: null, nameX: null };
};

const findIdentityToken = (tokens) => {
  for (let index = 0; index < tokens.length; index += 1) {
    const normalizedText = String(tokens[index].text || "")
      .replace(/[Xx•●]/g, "*")
      .replace(/\s/g, "");
    const fullMatch = normalizedText.match(/(?:^|\D)([1-9]\d{10})(?:\D|$)/);
    const maskedMatch = normalizedText.match(/(\d{2,5}\*{2,}\d{2,5})/);
    const value = fullMatch?.[1] || maskedMatch?.[1] || "";
    if (value) return { index, value };
  }
  return null;
};

const parseFallbackDayFromLine = (lineText) => {
  const lawDayMatch = lineText.match(/(?:0?5746|0?5510)\D{0,12}(\d{1,2})(?:\D|$)/);
  if (lawDayMatch) return parseDayValue(lawDayMatch[1]);
  const candidates = [...lineText.matchAll(/(?:^|\D)(\d{1,2})(?=\D|$)/g)]
    .map((match) => parseDayValue(match[1]))
    .filter((value) => value !== null);
  return candidates[candidates.length - 1] ?? null;
};

const parseFallbackNameFromLine = (lineText, identityValue) => {
  const identityIndex = lineText.indexOf(identityValue);
  if (identityIndex === -1) return "";
  const trailingText = lineText.slice(identityIndex + identityValue.length).trim();
  const match = trailingText.match(/^([A-Za-zÇĞİIÖŞÜçğıiöşü]+(?:\s+[A-Za-zÇĞİIÖŞÜçğıiöşü]+){1,5})/);
  return match?.[1]?.trim() || "";
};

export const parseSgkServicePages = (pages) => {
  const records = [];
  const warnings = [];
  let lawCodeDetected = false;

  pages.forEach((page) => {
    const pageText = page.lines
      .map((line) => line.tokens.map((token) => token.text).join(" "))
      .join("\n");
    if (/0?5746|0?5510/.test(pageText.replace(/\s/g, ""))) lawCodeDetected = true;
    const columns = detectColumnPositions(page.lines);

    page.lines.forEach((line, lineIndex) => {
      const identity = findIdentityToken(line.tokens);
      if (!identity) return;
      const rawLine = line.tokens.map((token) => token.text).join(" ");
      const normalizedLine = normalizeHeader(rawLine);
      if (normalizedLine.includes("TOPLAM")) return;

      const dayCandidates = line.tokens.filter(tokenIsDay);
      let dayToken = null;
      let confidence = "high";

      if (columns.dayX !== null && dayCandidates.length) {
        dayToken = [...dayCandidates].sort(
          (left, right) => Math.abs(left.x - columns.dayX) - Math.abs(right.x - columns.dayX)
        )[0];
        if (Math.abs(dayToken.x - columns.dayX) > 55) confidence = "low";
      } else {
        const lawIndex = line.tokens.findIndex((token) => /^(0?5746|0?5510)$/.test(token.text.replace(/\D/g, "")));
        dayToken = lawIndex >= 0
          ? line.tokens.slice(lawIndex + 1).find(tokenIsDay) || null
          : dayCandidates[dayCandidates.length - 1] || null;
        confidence = "low";
      }

      const personel = extractNameAfterIdentity(line.tokens, identity.index, dayToken) ||
        parseFallbackNameFromLine(rawLine, identity.value);
      const days = dayToken ? parseDayValue(dayToken.text) : parseFallbackDayFromLine(rawLine);
      if (!personel || days === null) confidence = "low";

      records.push({
        personel,
        tcKimlikNo: identity.value,
        days,
        confidence,
        rawLine,
        sourceRow: `Sayfa ${page.pageNumber}, satır ${lineIndex + 1}`
      });
    });
  });

  if (!lawCodeDetected) {
    warnings.push("PDF içinde 05746 veya 05510 kanun kodu doğrulanamadı; yüklenen belgeyi kontrol edin.");
  }

  const aggregated = aggregateRecords(records, "sgk");
  aggregated
    .filter((item) => item.confidence === "low")
    .forEach((item) => warnings.push(`${item.personel || item.tcKimlikNo} satırındaki Gün/eşleşme alanı kontrol edilmeli.`));

  return {
    records: aggregated,
    totalDays: aggregated.reduce((sum, item) => sum + Number(item.days || 0), 0),
    warnings,
    lawCodeDetected
  };
};

export const readSgkServicePdfFile = async (file) => {
  const pdfjs = await import("pdfjs-dist/build/pdf.mjs");
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  const document = await loadingTask.promise;
  const pages = [];
  let extractedCharacters = 0;

  for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
    const page = await document.getPage(pageNumber);
    const textContent = await page.getTextContent();
    extractedCharacters += textContent.items.reduce(
      (sum, item) => sum + String(item.str || "").trim().length,
      0
    );
    pages.push({ pageNumber, lines: groupPdfItemsIntoLines(textContent.items) });
  }

  if (extractedCharacters < 20) {
    throw new Error("PDF metin içermiyor veya taranmış görüntü biçiminde. Bu belge için OCR uygulanması gerekiyor.");
  }

  const parsed = parseSgkServicePages(pages);
  if (!parsed.records.length) {
    throw new Error("PDF içinde TC/maskeli TC içeren hizmet satırı bulunamadı. Belge düzeni veya Gün sütunu kontrol edilmeli.");
  }
  return parsed;
};

const nameSimilarity = (leftValue, rightValue) => {
  const left = normalizePersonName(leftValue);
  const right = normalizePersonName(rightValue);
  if (!left || !right) return 0;
  if (left === right) return 1;
  const leftTokens = new Set(left.split(" "));
  const rightTokens = new Set(right.split(" "));
  const intersection = [...leftTokens].filter((token) => rightTokens.has(token)).length;
  const union = new Set([...leftTokens, ...rightTokens]).size;
  return union ? intersection / union : 0;
};

const findAutomaticCandidate = (reportRecord, availableSgkRecords) => {
  const tcIdentity = normalizeTcIdentity(reportRecord.tcKimlikNo);
  if (tcIdentity.full) {
    const exactTc = availableSgkRecords.filter(
      (record) => normalizeTcIdentity(record.tcKimlikNo).full === tcIdentity.full
    );
    if (exactTc.length === 1) return { record: exactTc[0], method: "TC kimlik", confidence: "high" };
  }

  if (reportRecord.tcKimlikNo) {
    const maskedMatches = availableSgkRecords.filter((record) =>
      identitiesCompatible(reportRecord.tcKimlikNo, record.tcKimlikNo)
    );
    if (maskedMatches.length === 1) {
      return { record: maskedMatches[0], method: "Maskeli TC", confidence: "high" };
    }
  }

  const normalizedName = normalizePersonName(reportRecord.personel);
  const exactNames = availableSgkRecords.filter(
    (record) => normalizePersonName(record.personel) === normalizedName && normalizedName
  );
  if (exactNames.length === 1) return { record: exactNames[0], method: "Ad soyad", confidence: "high" };

  const ranked = availableSgkRecords
    .map((record) => ({ record, score: nameSimilarity(reportRecord.personel, record.personel) }))
    .filter((item) => item.score >= 0.66)
    .sort((left, right) => right.score - left.score);
  if (ranked.length && (ranked.length === 1 || ranked[0].score - ranked[1].score >= 0.2)) {
    return { record: ranked[0].record, method: "Benzer ad soyad", confidence: "low" };
  }

  return null;
};

const createComparisonRow = ({ reportRecord = null, sgkRecord = null, method = "", confidence = "high" }) => {
  const reportDays = reportRecord?.days ?? null;
  const sgkDays = sgkRecord?.days ?? null;
  const difference = reportDays === null || sgkDays === null ? null : reportDays - sgkDays;
  let status;

  if (!reportRecord) status = CHECK_STATUSES.MISSING_IN_REPORT;
  else if (!sgkRecord) status = CHECK_STATUSES.MISSING_IN_SGK;
  else if (
    (method !== "Manuel eşleştirme" && (confidence === "low" || sgkRecord.confidence === "low")) ||
    reportDays === null ||
    sgkDays === null
  ) {
    status = CHECK_STATUSES.REVIEW;
  } else if (difference === 0) status = CHECK_STATUSES.MATCHED;
  else status = CHECK_STATUSES.DAY_DIFFERENCE;

  return {
    id: `${reportRecord?.id || "none"}-${sgkRecord?.id || "none"}`,
    reportRecord,
    sgkRecord,
    reportPerson: reportRecord?.personel || "",
    sgkPerson: sgkRecord?.personel || "",
    reportTc: reportRecord?.tcKimlikNo || "",
    sgkTc: sgkRecord?.tcKimlikNo || "",
    reportDays,
    sgkDays,
    difference,
    method,
    status
  };
};

export const compare5746Records = ({ reportRecords, sgkRecords, manualMatches = {} }) => {
  const rows = [];
  const usedReportIds = new Set();
  const usedSgkIds = new Set();
  const blockedSgkIds = new Set(
    Object.entries(manualMatches)
      .filter(([, reportId]) => reportId === "__none__")
      .map(([sgkId]) => sgkId)
  );

  Object.entries(manualMatches).forEach(([sgkId, reportId]) => {
    if (!reportId || usedReportIds.has(reportId)) return;
    const reportRecord = reportRecords.find((record) => record.id === reportId);
    const sgkRecord = sgkRecords.find((record) => record.id === sgkId);
    if (!reportRecord || !sgkRecord) return;
    usedReportIds.add(reportId);
    usedSgkIds.add(sgkId);
    rows.push(createComparisonRow({ reportRecord, sgkRecord, method: "Manuel eşleştirme" }));
  });

  reportRecords
    .filter((record) => !usedReportIds.has(record.id))
    .forEach((reportRecord) => {
      const available = sgkRecords.filter(
        (record) => !usedSgkIds.has(record.id) && !blockedSgkIds.has(record.id)
      );
      const candidate = findAutomaticCandidate(reportRecord, available);
      if (!candidate) return;
      usedReportIds.add(reportRecord.id);
      usedSgkIds.add(candidate.record.id);
      rows.push(createComparisonRow({
        reportRecord,
        sgkRecord: candidate.record,
        method: candidate.method,
        confidence: candidate.confidence
      }));
    });

  reportRecords
    .filter((record) => !usedReportIds.has(record.id))
    .forEach((record) => rows.push(createComparisonRow({ reportRecord: record })));
  sgkRecords
    .filter((record) => !usedSgkIds.has(record.id))
    .forEach((record) => rows.push(createComparisonRow({ sgkRecord: record })));

  const order = Object.values(CHECK_STATUSES);
  rows.sort((left, right) => {
    const statusOrder = order.indexOf(left.status) - order.indexOf(right.status);
    if (statusOrder !== 0) return statusOrder;
    return String(left.reportPerson || left.sgkPerson).localeCompare(
      String(right.reportPerson || right.sgkPerson),
      "tr"
    );
  });

  return {
    rows,
    totals: {
      reportDays: reportRecords.reduce((sum, record) => sum + Number(record.days || 0), 0),
      sgkDays: sgkRecords.reduce((sum, record) => sum + Number(record.days || 0), 0)
    },
    counts: Object.values(CHECK_STATUSES).reduce((result, status) => ({
      ...result,
      [status]: rows.filter((row) => row.status === status).length
    }), {})
  };
};

export const download5746ComparisonExcel = ({ comparison, reportFileName, sgkFileName }) => {
  const workbook = XLSX.utils.book_new();
  const summaryRows = [
    ["Beyan Sonrası 5746 Kontrolü"],
    ["Bizim Rapor Excel’i", reportFileName || ""],
    ["SGK Hizmet Listesi PDF’i", sgkFileName || ""],
    [],
    ["Toplam bizim 5746 günümüz", comparison.totals.reportDays],
    ["Toplam SGK gün sayısı", comparison.totals.sgkDays],
    ["Toplam fark", comparison.totals.reportDays - comparison.totals.sgkDays],
    [],
    ...Object.values(CHECK_STATUSES).map((status) => [status, comparison.counts[status] || 0])
  ];
  const detailRows = comparison.rows.map((row) => ({
    "Durum": row.status,
    "Bizim Rapordaki Personel": row.reportPerson,
    "SGK’daki Personel": row.sgkPerson,
    "Bizim Rapordaki TC": row.reportTc,
    "SGK’daki TC": row.sgkTc,
    "Bizim 5746 Gün": row.reportDays ?? "",
    "SGK Gün": row.sgkDays ?? "",
    "Gün Farkı": row.difference ?? "",
    "Eşleşme Yöntemi": row.method,
    "Bizim Kaynak Satırı": row.reportRecord?.sourceRows?.join(", ") || "",
    "SGK Kaynak Satırı": row.sgkRecord?.sourceRows?.join(", ") || ""
  }));

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
  summarySheet["!cols"] = [{ wch: 34 }, { wch: 28 }];
  const detailSheet = XLSX.utils.json_to_sheet(detailRows);
  detailSheet["!cols"] = [
    { wch: 26 }, { wch: 28 }, { wch: 28 }, { wch: 18 }, { wch: 18 },
    { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 22 }, { wch: 22 }, { wch: 22 }
  ];
  detailSheet["!autofilter"] = { ref: `A1:K${Math.max(detailRows.length + 1, 2)}` };
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Özet");
  XLSX.utils.book_append_sheet(workbook, detailSheet, "Karşılaştırma");

  const dateText = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `Beyan_Sonrasi_5746_Kontrolu_${dateText}.xlsx`);
};
